import React, { useEffect, useState, useMemo } from "react";
import { http } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import EquipoPreview from "./EquipoPreview";
import {
  Search,
  Monitor,
  RefreshCw,
  HardDrive,
  MapPin,
  CheckCircle,
  Clock,
  AlertCircle,
  Layers,
  ChevronRight,
  ArrowLeft,
  Eye,
  Tag,
  Hash,
  Cpu,
  MemoryStick,
} from "lucide-react";

// ── helpers ──────────────────────────────────────────────────────────────────
function getCategoriaEquipo(tipo) {
  if (!tipo) return "generico";
  const t = tipo.toLowerCase();
  if (["pc", "notebook", "minipc", "servidor", "tablet", "computador", "computadora", "laptop"].some(k => t.includes(k))) return "computador";
  if (["router", "ap", "switch", "firewall", "access point"].some(k => t.includes(k))) return "red";
  if (t.includes("impresora")) return "impresora";
  if (t.includes("ups")) return "ups";
  return "generico";
}

const ESTADO_CONFIG = {
  operativo: { color: "bg-green-100 text-green-800", icon: CheckCircle },
  activo:    { color: "bg-green-100 text-green-800", icon: CheckCircle },
  mantenimiento: { color: "bg-yellow-100 text-yellow-800", icon: Clock },
  en_reparacion: { color: "bg-orange-100 text-orange-800", icon: Clock },
  inactivo:  { color: "bg-gray-100 text-gray-700", icon: AlertCircle },
  dado_de_baja: { color: "bg-red-100 text-red-700", icon: AlertCircle },
};

function EstadoBadge({ estado }) {
  const cfg = ESTADO_CONFIG[estado] || ESTADO_CONFIG.inactivo;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full ${cfg.color}`}>
      <Icon className="w-3 h-3" />{estado || "—"}
    </span>
  );
}

// ── componente ────────────────────────────────────────────────────────────────
export default function MisEquipos({ equipoIdFromUrl, onEquipoViewed }) {
  const { user } = useAuth();
  const [equipos, setEquipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Navegación
  const [vista, setVista] = useState("sucursales"); // "sucursales" | "equipos"
  const [sucursalActiva, setSucursalActiva] = useState(null); // null = todas

  // Búsqueda dentro de equipos
  const [busqueda, setBusqueda] = useState("");

  // Preview modal
  const [equipoPreview, setEquipoPreview] = useState(null);

  useEffect(() => { cargarMisEquipos(); }, []);

  // Abrir equipo desde URL
  useEffect(() => {
    if (equipoIdFromUrl && equipos.length > 0) {
      const eq = equipos.find(e => e._id === equipoIdFromUrl || e.id === equipoIdFromUrl);
      if (eq) {
        setSucursalActiva(null);
        setVista("equipos");
        setEquipoPreview(eq);
        window.history.replaceState({}, "", "/");
        if (onEquipoViewed) onEquipoViewed();
      }
    }
  }, [equipoIdFromUrl, equipos]);

  async function cargarMisEquipos() {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const res = await http.get("/cliente/mis-equipos", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEquipos(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.response?.data?.detail || "Error al cargar los equipos");
      setEquipos([]);
    } finally {
      setLoading(false);
    }
  }

  // ── computed ─────────────────────────────────────────────────────────────
  const sucursales = useMemo(() => {
    const set = new Set(equipos.map(e => e.sucursal).filter(Boolean));
    return [...set].sort();
  }, [equipos]);

  const equiposActuales = useMemo(() => {
    let lista = sucursalActiva
      ? equipos.filter(e => e.sucursal === sucursalActiva)
      : equipos;
    if (busqueda.trim()) {
      const t = busqueda.toLowerCase();
      lista = lista.filter(e =>
        [e.nombre, e.marca, e.modelo, e.numero_serie, e.descripcion, e.ubicacion, e.sucursal]
          .some(v => v && v.toString().toLowerCase().includes(t))
      );
    }
    return lista;
  }, [equipos, sucursalActiva, busqueda]);

  const equiposPorTipo = useMemo(() => {
    const map = {};
    equiposActuales.forEach(e => {
      const tipo = e.tipo_equipo || "Sin tipo";
      if (!map[tipo]) map[tipo] = [];
      map[tipo].push(e);
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [equiposActuales]);

  const stats = useMemo(() => ({
    total: equipos.length,
    operativos: equipos.filter(e => e.estado === "operativo" || e.estado === "activo").length,
    mantenimiento: equipos.filter(e => e.estado === "mantenimiento" || e.estado === "en_reparacion").length,
    sucursales: sucursales.length,
  }), [equipos, sucursales]);

  // ── navigation ──────────────────────────────────────────────────────────
  function abrirSucursal(suc) {
    setSucursalActiva(suc);
    setBusqueda("");
    setVista("equipos");
  }

  function volverASucursales() {
    setSucursalActiva(null);
    setBusqueda("");
    setVista("sucursales");
  }

  // ── render ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600" />
          <span className="text-gray-600">Cargando equipos...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 max-w-lg mx-auto mt-6">
        <div className="flex items-center gap-3 mb-3">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <p className="font-semibold text-red-800">Error al cargar equipos</p>
        </div>
        <p className="text-red-700 text-sm mb-4">{error}</p>
        <button onClick={cargarMisEquipos} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm">
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* EquipoPreview maneja su propio portal */}
      {equipoPreview && <EquipoPreview equipo={equipoPreview} onClose={() => setEquipoPreview(null)} />}

      <div className="max-w-5xl mx-auto">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            {vista === "sucursales" && (
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <HardDrive className="w-6 h-6 text-emerald-600" />Mis Equipos
              </h1>
            )}
            {vista === "equipos" && (
              <div>
                {sucursales.length > 0 && (
                  <button onClick={volverASucursales} className="flex items-center gap-1 text-sm text-emerald-600 hover:text-emerald-800 mb-1">
                    <ArrowLeft className="w-4 h-4" />Sucursales
                  </button>
                )}
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Layers className="w-6 h-6 text-emerald-600" />
                  {sucursalActiva || "Todos los equipos"}
                </h1>
              </div>
            )}
          </div>
          <button onClick={cargarMisEquipos} className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm">
            <RefreshCw className="w-4 h-4" />Actualizar
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2.5">
              <div className="p-2 bg-emerald-100 rounded-lg"><HardDrive className="w-4 h-4 text-emerald-600" /></div>
              <div><p className="text-xs text-gray-500">Total equipos</p><p className="text-xl font-bold text-gray-900">{stats.total}</p></div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2.5">
              <div className="p-2 bg-green-100 rounded-lg"><CheckCircle className="w-4 h-4 text-green-600" /></div>
              <div><p className="text-xs text-gray-500">Operativos</p><p className="text-xl font-bold text-gray-900">{stats.operativos}</p></div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2.5">
              <div className="p-2 bg-yellow-100 rounded-lg"><Clock className="w-4 h-4 text-yellow-600" /></div>
              <div><p className="text-xs text-gray-500">Mantenimiento</p><p className="text-xl font-bold text-gray-900">{stats.mantenimiento}</p></div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2.5">
              <div className="p-2 bg-purple-100 rounded-lg"><MapPin className="w-4 h-4 text-purple-600" /></div>
              <div><p className="text-xs text-gray-500">Sucursales</p><p className="text-xl font-bold text-gray-900">{stats.sucursales}</p></div>
            </div>
          </div>
        </div>

        {/* ══ VISTA: SUCURSALES ══════════════════════════════════════════ */}
        {vista === "sucursales" && (
          <>
            {equipos.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
                <HardDrive className="w-14 h-14 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-700 mb-1">Sin equipos registrados</h3>
                <p className="text-sm text-gray-500">Los equipos asignados a su cuenta aparecerán aquí.</p>
              </div>
            ) : sucursales.length === 0 ? (
              <button onClick={() => abrirSucursal(null)}
                className="w-full text-left bg-white rounded-xl border-2 border-emerald-100 hover:border-emerald-400 hover:shadow-md transition-all p-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <Layers className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-800">Todos mis equipos</div>
                    <div className="text-sm text-gray-500">{equipos.length} equipo{equipos.length !== 1 ? "s" : ""}</div>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </button>
            ) : (
              <div className="space-y-2">
                <button onClick={() => abrirSucursal(null)}
                  className="w-full text-left flex items-center justify-between p-4 bg-white rounded-xl border-2 border-emerald-100 hover:border-emerald-400 hover:shadow-sm transition-all">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <Layers className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-800 text-sm">Todas las sucursales</div>
                      <div className="text-xs text-gray-500">{equipos.length} equipo{equipos.length !== 1 ? "s" : ""}</div>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>
                {sucursales.map(suc => {
                  const cnt = equipos.filter(e => e.sucursal === suc).length;
                  return (
                    <button key={suc} onClick={() => abrirSucursal(suc)}
                      className="w-full text-left flex items-center justify-between p-4 bg-white rounded-xl border-2 border-gray-100 hover:border-emerald-300 hover:shadow-sm transition-all">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center">
                          <MapPin className="w-4 h-4 text-gray-500" />
                        </div>
                        <div>
                          <div className="font-semibold text-gray-800 text-sm">{suc}</div>
                          <div className="text-xs text-gray-500">{cnt} equipo{cnt !== 1 ? "s" : ""}</div>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ══ VISTA: EQUIPOS (agrupados por tipo) ══════════════════════ */}
        {vista === "equipos" && (
          <>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input type="text" value={busqueda} onChange={e => setBusqueda(e.target.value)}
                placeholder="Buscar por nombre, marca, modelo, serie..."
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 text-sm bg-white" />
            </div>

            {equiposActuales.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
                <HardDrive className="w-14 h-14 text-gray-300 mx-auto mb-4" />
                <p className="font-medium text-gray-600">{busqueda ? "Sin resultados para la búsqueda" : "No hay equipos en esta sucursal"}</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between text-sm text-gray-500 pb-2 border-b border-gray-200">
                  <span>
                    <span className="font-semibold text-gray-700">{equiposActuales.length}</span>
                    {" "}equipo{equiposActuales.length !== 1 ? "s" : ""}
                    {busqueda && ` · "${busqueda}"`}
                  </span>
                  <span className="flex items-center gap-1 text-green-600">
                    <CheckCircle className="w-3.5 h-3.5" />
                    {equiposActuales.filter(e => e.estado === "operativo" || e.estado === "activo").length} operativos
                  </span>
                </div>

                {equiposPorTipo.map(([tipo, items]) => (
                  <div key={tipo} className="border border-gray-200 rounded-xl overflow-hidden bg-white">
                    <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                      <Layers className="w-4 h-4 text-gray-500" />
                      <span className="font-semibold text-gray-700 text-sm">{tipo}</span>
                      <span className="ml-auto text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full font-medium">{items.length}</span>
                    </div>

                    <div className="divide-y divide-gray-100">
                      {items.map(eq => {
                        const cat = getCategoriaEquipo(eq.tipo_equipo);
                        return (
                          <div key={eq._id || eq.id} className="p-4 hover:bg-emerald-50 transition-colors">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex items-center gap-3 min-w-0">
                                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                                  <Monitor className="w-5 h-5 text-emerald-600" />
                                </div>
                                <div className="min-w-0">
                                  <div className="font-semibold text-gray-900 text-sm truncate">
                                    {eq.nombre || `${eq.marca || ""} ${eq.modelo || ""}`.trim() || "—"}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {[eq.marca, eq.modelo].filter(Boolean).join(" · ")}
                                  </div>
                                  {eq.sucursal && sucursalActiva === null && (
                                    <div className="flex items-center gap-1 text-xs text-emerald-500 mt-0.5">
                                      <MapPin className="w-3 h-3" />{eq.sucursal}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex flex-col items-end gap-2 flex-shrink-0">
                                <EstadoBadge estado={eq.estado} />
                                <button onClick={() => setEquipoPreview(eq)}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs hover:bg-emerald-700 transition-colors">
                                  <Eye className="w-3.5 h-3.5" />Ver detalles
                                </button>
                              </div>
                            </div>

                            <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5">
                              {eq.numero_serie && (
                                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                                  <Hash className="w-3 h-3 flex-shrink-0" />
                                  <span className="font-mono truncate">{eq.numero_serie}</span>
                                </div>
                              )}
                              {cat === "computador" && eq.procesador && (
                                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                                  <Cpu className="w-3 h-3 flex-shrink-0" />
                                  <span className="truncate">{eq.procesador}</span>
                                </div>
                              )}
                              {cat === "computador" && eq.ram && (
                                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                                  <MemoryStick className="w-3 h-3 flex-shrink-0" />
                                  <span className="truncate">{eq.ram}</span>
                                </div>
                              )}
                              {eq.ubicacion && (
                                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                                  <MapPin className="w-3 h-3 flex-shrink-0" />
                                  <span className="truncate">{eq.ubicacion}</span>
                                </div>
                              )}
                              {cat === "impresora" && eq.tipo_impresion && (
                                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                                  <Tag className="w-3 h-3 flex-shrink-0" />
                                  <span>{eq.tipo_impresion}</span>
                                </div>
                              )}
                              {cat === "ups" && eq.capacidad_va && (
                                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                                  <Tag className="w-3 h-3 flex-shrink-0" />
                                  <span>{eq.capacidad_va} VA</span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

      </div>
    </div>
  );
}
